import { useCallback, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaFire,
  FaPlane,
  FaRegNewspaper
} from 'react-icons/fa'

import api from 'services/api'
import * as S from './style'

type RadarItem = {
  id: string
  type: 'offer' | 'partner' | 'content'
  category?: string | null
  title: string
  slug: string
  subtitle?: string | null
  excerpt?: string | null
  coverImage?: string | null
  partnerName?: string | null
  partnerLogo?: string | null
  externalUrl?: string | null
  ctaLabel?: string | null
  price?: number | null
  oldPrice?: number | null
  currency?: string
  metaLine?: string | null
  publishedAt?: string | null
  readingMinutes?: number | null
}

type RadarHome = {
  offers: RadarItem[]
  partners: RadarItem[]
  contents: RadarItem[]
}

const FALLBACK = '/images/itinerary-banner.png'

const externalHref = (value?: string | null) => {
  const url = String(value || '').trim()
  if (!url) return ''

  if (/^https?:\/\//i.test(url)) return url
  if (/^\/\//.test(url)) return `https:${url}`

  return `https://${url.replace(/^\/+/, '')}`
}

const money = (value?: number | null, currency = 'BRL') =>
  value == null
    ? ''
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0
      }).format(value)

const formatDate = (value?: string | null) => {
  if (!value) return ''
  return new Date(value).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}

const useAutoCarousel = (itemCount: number) => {
  const ref = useRef<HTMLDivElement>(null)

  const move = useCallback((direction: 1 | -1) => {
    const element = ref.current
    if (!element) return

    const maxScroll = Math.max(0, element.scrollWidth - element.clientWidth)
    if (maxScroll <= 4) return

    if (direction > 0 && element.scrollLeft >= maxScroll - 8) {
      element.scrollTo({ left: 0, behavior: 'smooth' })
      return
    }

    if (direction < 0 && element.scrollLeft <= 8) {
      element.scrollTo({ left: maxScroll, behavior: 'smooth' })
      return
    }

    element.scrollBy({
      left: direction * Math.max(260, element.clientWidth * 0.92),
      behavior: 'smooth'
    })
  }, [])

  useEffect(() => {
    if (itemCount <= 1) return

    const interval = window.setInterval(() => move(1), 6000)
    return () => window.clearInterval(interval)
  }, [itemCount, move])

  return { ref, move }
}

const Radar = () => {
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['radar-home'],
    queryFn: async () => {
      const response = await api.get<RadarHome>('/radar', {
        params: { _t: Date.now() },
        timeout: 10000
      })
      return response.data
    },
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 1
  })

  const offers = data?.offers ?? []
  const partners = data?.partners ?? []
  const contents = data?.contents ?? []
  const lead = contents[0]
  const sideNews = contents.slice(1, 4)
  const offerCarousel = useAutoCarousel(offers.length)
  const partnerCarousel = useAutoCarousel(partners.length)

  return (
    <S.Page>
      <S.Hero>
        <S.HeroContent>
          <S.Eyebrow>Radar RedSterna</S.Eyebrow>
          <h1>
            Mais viagens.
            <br />
            Melhores histórias.
          </h1>
          <p>
            Promoções, pacotes, hospedagens e notícias para você explorar o
            mundo gastando menos.
          </p>
        </S.HeroContent>
      </S.Hero>

      {isLoading ? (
        <S.Section>
          <S.Empty>Carregando oportunidades do Radar...</S.Empty>
        </S.Section>
      ) : isError ? (
        <S.Section>
          <S.Empty role="alert">
            Não foi possível carregar o Radar agora.
            <S.RetryButton
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? 'Tentando novamente...' : 'Tentar novamente'}
            </S.RetryButton>
          </S.Empty>
        </S.Section>
      ) : (
        <>
          <S.Section>
            <S.SectionHeader>
              <div>
                <h2>
                  <FaFire /> Ofertas em destaque
                </h2>
                <p>Oportunidades selecionadas para a sua próxima viagem.</p>
              </div>
            </S.SectionHeader>

            {offers.length ? (
              <S.CarouselShell>
                <S.CarouselViewport ref={offerCarousel.ref}>
                  {offers.slice(0, 6).map((item) => (
                    <S.CarouselItem key={item.id}>
                      <S.OfferCard>
                        <S.CardImage
                          src={item.coverImage || FALLBACK}
                          alt={item.title}
                        />
                        <S.OfferBody>
                          {item.category && <S.Badge>{item.category}</S.Badge>}
                          <h3>{item.title}</h3>
                          {item.price != null && item.price > 0 && (
                            <S.PriceRow>
                              <strong>
                                {money(item.price, item.currency)}
                              </strong>
                              {item.oldPrice != null && item.oldPrice > 0 && (
                                <del>{money(item.oldPrice, item.currency)}</del>
                              )}
                            </S.PriceRow>
                          )}
                          {item.metaLine &&
                            !/confirme disponibilidade antes de publicar/i.test(
                              item.metaLine
                            ) && <S.Meta>{item.metaLine}</S.Meta>}
                          {item.partnerName && (
                            <S.Supplier>{item.partnerName}</S.Supplier>
                          )}
                          {item.externalUrl ? (
                            <S.PrimaryAnchor
                              href={externalHref(item.externalUrl)}
                              target="_blank"
                              rel="noopener noreferrer sponsored"
                            >
                              {item.ctaLabel || 'Ver oferta'} <FaArrowRight />
                            </S.PrimaryAnchor>
                          ) : (
                            <S.PrimaryLink to={'/radar/' + item.slug}>
                              {item.ctaLabel || 'Ver oferta'} <FaArrowRight />
                            </S.PrimaryLink>
                          )}
                        </S.OfferBody>
                      </S.OfferCard>
                    </S.CarouselItem>
                  ))}
                </S.CarouselViewport>

                {offers.length > 1 && (
                  <>
                    <S.CarouselButton
                      type="button"
                      $side="left"
                      aria-label="Ofertas anteriores"
                      onClick={() => offerCarousel.move(-1)}
                    >
                      <FaChevronLeft />
                    </S.CarouselButton>
                    <S.CarouselButton
                      type="button"
                      $side="right"
                      aria-label="Próximas ofertas"
                      onClick={() => offerCarousel.move(1)}
                    >
                      <FaChevronRight />
                    </S.CarouselButton>
                  </>
                )}
              </S.CarouselShell>
            ) : (
              <S.Empty>
                As primeiras ofertas selecionadas aparecerão aqui em breve.
              </S.Empty>
            )}
          </S.Section>

          <S.PartnerBand>
            <S.Section>
              <S.SectionHeader>
                <div>
                  <h2>
                    <FaPlane /> Pacotes e hospedagens de parceiros
                  </h2>
                  <p>
                    Viagens completas com parceiros selecionados da RedSterna.
                  </p>
                </div>
              </S.SectionHeader>

              {partners.length ? (
                <S.CarouselShell>
                  <S.CarouselViewport ref={partnerCarousel.ref}>
                    {partners.slice(0, 6).map((item) => (
                      <S.CarouselItem key={item.id}>
                        <S.PartnerCard>
                          <S.PartnerImage
                            src={item.coverImage || FALLBACK}
                            alt={item.title}
                          />
                          <S.PartnerContent>
                            <S.PartnerTop>
                              {item.partnerLogo ? (
                                <img
                                  src={item.partnerLogo}
                                  alt={item.partnerName || ''}
                                />
                              ) : (
                                <span>{item.partnerName || 'Parceiro'}</span>
                              )}
                            </S.PartnerTop>
                            <h3>{item.title}</h3>
                            {item.metaLine && <S.Meta>{item.metaLine}</S.Meta>}
                            <S.PartnerBottom>
                              <div>
                                {item.price != null && item.price > 0 && (
                                  <strong>
                                    {money(item.price, item.currency)}
                                  </strong>
                                )}
                                {item.subtitle && (
                                  <small>{item.subtitle}</small>
                                )}
                              </div>
                              {item.externalUrl ? (
                                <S.SecondaryAnchor
                                  href={externalHref(item.externalUrl)}
                                  target="_blank"
                                  rel="noopener noreferrer sponsored"
                                >
                                  {item.ctaLabel || 'Ver pacote'}
                                </S.SecondaryAnchor>
                              ) : (
                                <S.SecondaryLink to={'/radar/' + item.slug}>
                                  {item.ctaLabel || 'Ver pacote'}
                                </S.SecondaryLink>
                              )}
                            </S.PartnerBottom>
                          </S.PartnerContent>
                        </S.PartnerCard>
                      </S.CarouselItem>
                    ))}
                  </S.CarouselViewport>

                  {partners.length > 1 && (
                    <>
                      <S.CarouselButton
                        type="button"
                        $side="left"
                        aria-label="Parceiros anteriores"
                        onClick={() => partnerCarousel.move(-1)}
                      >
                        <FaChevronLeft />
                      </S.CarouselButton>
                      <S.CarouselButton
                        type="button"
                        $side="right"
                        aria-label="Próximos parceiros"
                        onClick={() => partnerCarousel.move(1)}
                      >
                        <FaChevronRight />
                      </S.CarouselButton>
                    </>
                  )}
                </S.CarouselShell>
              ) : (
                <S.Empty>
                  Pacotes e hospedagens de parceiros aparecerão aqui.
                </S.Empty>
              )}
            </S.Section>
          </S.PartnerBand>

          <S.Section>
            <S.SectionHeader>
              <div>
                <h2>
                  <FaRegNewspaper /> Notícias e dicas de viagem
                </h2>
                <p>
                  Conteúdos para informar, inspirar e ajudar a planejar melhor.
                </p>
              </div>
              {lead && (
                <S.SectionAction to="/radar/noticias">
                  Ver todas as notícias <FaArrowRight />
                </S.SectionAction>
              )}
            </S.SectionHeader>

            {lead ? (
              <S.NewsLayout>
                <S.LeadStory to={'/radar/' + lead.slug}>
                  <S.LeadImage
                    src={lead.coverImage || FALLBACK}
                    alt={lead.title}
                  />
                  <S.LeadText>
                    {lead.category && <S.Badge>{lead.category}</S.Badge>}
                    <h3>{lead.title}</h3>
                    {lead.excerpt && <p>{lead.excerpt}</p>}
                    <S.NewsMeta>
                      {formatDate(lead.publishedAt)}
                      {lead.readingMinutes
                        ? ' · ' + lead.readingMinutes + ' min de leitura'
                        : ''}
                    </S.NewsMeta>
                  </S.LeadText>
                </S.LeadStory>

                <S.NewsList>
                  {sideNews.map((item) => (
                    <S.NewsItem key={item.id} to={'/radar/' + item.slug}>
                      <img src={item.coverImage || FALLBACK} alt={item.title} />
                      <div>
                        {item.category && (
                          <S.SmallBadge>{item.category}</S.SmallBadge>
                        )}
                        <h4>{item.title}</h4>
                        <S.NewsMeta>
                          {formatDate(item.publishedAt)}
                          {item.readingMinutes
                            ? ' · ' + item.readingMinutes + ' min'
                            : ''}
                        </S.NewsMeta>
                      </div>
                    </S.NewsItem>
                  ))}
                </S.NewsList>
              </S.NewsLayout>
            ) : (
              <S.Empty>
                Notícias, guias e dicas publicados no Admin aparecerão aqui.
              </S.Empty>
            )}
          </S.Section>
        </>
      )}

      <S.Newsletter>
        <div>
          <S.Eyebrow>Fique por dentro</S.Eyebrow>
          <h2>Receba as melhores oportunidades</h2>
          <p>
            Em breve: novidades, promoções e conteúdos selecionados no seu
            e-mail.
          </p>
        </div>
        <S.NewsletterForm onSubmit={(event) => event.preventDefault()}>
          <input type="email" placeholder="Seu melhor e-mail" disabled />
          <button type="submit" disabled>
            Quero receber
          </button>
        </S.NewsletterForm>
      </S.Newsletter>
    </S.Page>
  )
}

export default Radar
